import { api } from '../services/api';

/**
 * Several jobs in one day, offered as ONE open shift.
 *
 * The owner, Oct 8 2026: "can you change the open shift thing somehow implement
 * ith multiple dif oibs in one day to get the guys to move to the next job
 * without having to request another open shift thing myabe a group or route or
 * something". The day's loose jobs go on a one-day route and the route is what
 * is offered: one claim puts that person on the route for the day, and a route
 * already has the order, Start Route and the next stop. The rules live on the
 * server (services/openShift, routes/jobs.js); these are the words and the
 * grouping the three screens share — the crew's Open tab, the Calendar's
 * "Route loose jobs" sheet and the job page's Open shift panel.
 */

/** "Sat, Oct 10" — the way the open-shift text says the day. */
export function shortDay(iso: string | null | undefined): string {
  const day = String(iso || '').slice(0, 10);
  const d = new Date(day + 'T12:00:00');
  return isNaN(d.getTime()) ? day : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** "3 jobs" / "1 job". */
export const jobsWord = (n: number): string => `${n} job${n === 1 ? '' : 's'}`;

/** "all 3 jobs" / "both jobs" / "this job" — how a sentence points at the jobs of one open shift. */
export function allJobsWord(n: number): string {
  return n <= 1 ? 'this job' : n === 2 ? 'both jobs' : `all ${n} jobs`;
}

/** "Luis", "Luis and Sam", "Luis, Sam and Maria". */
export function namesWord(names: (string | null | undefined)[]): string {
  const list = names.map(n => String(n || '').trim()).filter(Boolean);
  return list.length <= 1 ? (list[0] || '') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * What was typed in a "People needed" box, as a number — or null while the box
 * is empty or holds nothing usable, when the number already set stays as it is.
 * Digits only, at least 1, never more than `max`. The box used to snap an empty
 * field straight back to 1, so on a phone the 1 could not be cleared: typing 2
 * gave 21, and every one of the 21 would be on every job of the day.
 */
export function readPeopleNeeded(text: string | null | undefined, max = 20): number | null {
  const digits = String(text ?? '').replace(/\D/g, '');
  if (!digits) return null;
  const n = Number(digits);
  return n >= 1 ? Math.min(n, max) : null;
}

/**
 * Why a job can't go in, shortened for a list whose heading already says
 * "can't go in an open shift": the server's sentence ends ", so it can't go in
 * an open shift with other jobs." and only the part before that is new. A
 * sentence worded another way is shown whole.
 */
export function whyNotWord(sentence: string | null | undefined): string {
  const s = String(sentence || '').trim();
  const cut = s.split(", so it can't")[0];
  return cut === s ? s : `${cut}.`;
}

/**
 * The crew's Open tab lists one card per OFFER, not per job: the rows of a day
 * offered as one open shift (route_open_shift = 1, same route, same day) come
 * back as one group, in the order the server sent them — the order they are
 * driven. Any other open shift is a group of its own.
 */
export interface OpenOffer { key: string; jobs: any[] }
export function groupOpenShifts(rows: any[] | null | undefined): OpenOffer[] {
  const out: OpenOffer[] = [];
  const days = new Map<string, OpenOffer>();
  for (const j of rows || []) {
    if (Number(j?.route_open_shift) !== 1 || j?.route_id == null) { out.push({ key: `job-${j.id}`, jobs: [j] }); continue; }
    const key = `day-${j.route_id}-${String(j.scheduled_date || '').slice(0, 10)}`;
    let offer = days.get(key);
    if (!offer) { offer = { key, jobs: [] }; days.set(key, offer); out.push(offer); }
    offer.jobs.push(j);
  }
  return out;
}

/**
 * The jobs on a day that could go out together as one open shift
 * (GET /routes/loose-jobs?offer=1): the loose ones and the ones already
 * offered one at a time. `cant_offer` is null when a job may go in, otherwise
 * the server's plain sentence saying why not; `open_shift` marks one that is
 * already offered on its own. null when the server did not answer with the
 * offer list (an older build that ignores ?offer=1) — then nothing is offered
 * from here rather than a plain route being built by mistake.
 */
export interface OfferJob {
  job_id: number; title: string | null; service_type: string | null; customer_name: string | null;
  address: string | null; city: string | null; minutes: number | null; time_start: string | null;
  assigned_name: string | null; pinned: boolean; firm: boolean; project: boolean;
  open_shift: boolean; cant_offer: string | null;
}
export const offerJobsKey = (date: string) => ['loose-jobs', date, 'offer'];
export function fetchOfferJobs(date: string): Promise<OfferJob[] | null> {
  return api.get('/routes/loose-jobs', { params: { date, offer: 1 } })
    .then(r => (r.data?.offer === true ? (r.data.jobs as OfferJob[]) : null));
}
